import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { noop, Subject } from 'rxjs';
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  BASE_CUSTOMER_OFFER_STATUS_OPTIONS,
  BASE_PERMISSIONS,
  BASE_STICKY_HEADER_SCROLL_DISTANCE,
  BaseButtonSize,
  BaseCoreState,
  BaseCustomerInquiryFilters,
  BaseCustomerInquiryOfferInterface,
  BaseFilterService,
  BaseHelpersService,
  baseInquiryActions,
  BaseInquiryDate,
  BaseInquiryDateService,
  BaseInquiryOfferOrderKey,
  BaseInquiryOffersFilters,
  baseInquirySelectors,
  BaseInquiryService,
  BaseInquiryShiftService,
  BaseInquiryStatus,
  BaseInquiryStatusValue,
  BaseInquiryType,
  BaseModalService,
  BaseOrderDirectionEnum,
  BaseQueryParamService,
  BaseRolesService,
  InquiryDateResponse,
  InquiryOffersPagination,
  MetaPagination,
  OffersCountResponse,
  PspBranchFilterInfoResponse,
  QualificationShortInfoResponse,
  ShiftTimeAndFlexResponse,
  ShiftTimeResponse,
} from 'base';
import { BaseCustomerInquiryOffersSortKeysInterface } from '../../_interfaces/base-customer-inquiry-offers-sort-keys.interface';
import { BaseCustomerInquiryExperienceOptionsConst } from '../../_const/base-customer-inquiry-offers-filters-options.const';
import { BASE_CUSTOMER_WORK_COUNCIL_APPROVAL_OPTIONS } from '../../../offers/_enums/base-customer-offers.enum';
import { BaseCustomerInquiryService } from '../../inquiry.service';
import { BaseCustomerServiceProviderSelectService } from '../../../../shared/_services/base-customer-service-provider-select.service';
import { BaseCustomerSharedService } from '../../../../shared/_services/base-customer-shared.service';

@Component({
  selector: 'base-customer-inquiry-offers',
  templateUrl: 'base-customer-inquiry-offers.component.html',
})
export class BaseCustomerInquiryOffersComponent implements OnInit, OnDestroy {
  @ViewChild('closeInquiryModal', { static: true }) closeInquiryModal: ElementRef;
  @ViewChild('staffRequest', { static: true }) staffRequest: ElementRef;
  @ViewChild('inquiredServiceProvidersModal') inquiredServiceProvidersModal: ElementRef;
  @ViewChild('serviceProviderSelectModal', { static: true }) serviceProviderSelectModal: ElementRef;

  offers: BaseCustomerInquiryOfferInterface[] = [];
  filteredOffers: BaseCustomerInquiryOfferInterface[] = [];
  offersCount: OffersCountResponse;
  mainQualifications: QualificationShortInfoResponse[];
  branches: PspBranchFilterInfoResponse[];
  inquiry: BaseInquiryType | undefined;
  copiedInquiry: BaseInquiryType | undefined;
  shiftTimes: (ShiftTimeResponse & { index: number; label?: string })[] = [];
  shiftTimesWithFlex: (ShiftTimeAndFlexResponse & { index: number; label?: string })[] = [];
  listOrderKey: BaseInquiryOfferOrderKey | undefined;
  listOrderDirection: BaseOrderDirectionEnum | undefined;
  canPublishInquiry: boolean;
  hasCloseInquiryPermission: boolean;
  datesArray: BaseInquiryDate[] = [];
  inquiryListActiveFilters: { [key in any]: any } = {};
  isSidebarVisible = false;
  hasFiltersValues = false;
  isSticky = false;
  stickyHeaderScrollDistance = BASE_STICKY_HEADER_SCROLL_DISTANCE;
  pagination: MetaPagination;
  modalRef: NgbModalRef;
  onInquiryCloseModalRef: NgbModalRef;
  inquiredServiceProvidersModalRef: NgbModalRef;
  serviceProviderSelectModalRef: NgbModalRef;
  unpublishedInquiryId: number;
  buttonSizes = BaseButtonSize;
  baseInquiryStatus = BaseInquiryStatus;
  inquiryStatusValue = BaseInquiryStatusValue;
  baseInquiryExperienceOption = BaseCustomerInquiryExperienceOptionsConst;
  baseOfferStatusOption = BASE_CUSTOMER_OFFER_STATUS_OPTIONS;
  baseCustomerWCApprovalOptions = BASE_CUSTOMER_WORK_COUNCIL_APPROVAL_OPTIONS;
  filtersForm = this.fb.group({
    [BaseInquiryOffersFilters.status]: undefined,
    [BaseInquiryOffersFilters.workCouncilStatus]: undefined,
    [BaseInquiryOffersFilters.rating]: 0,
    [BaseInquiryOffersFilters.experience]: undefined,
    [BaseInquiryOffersFilters.pspBranches]: [[] as number[]],
    [BaseInquiryOffersFilters.mainQualifications]: [[] as number[]],
    [BaseCustomerInquiryFilters.search]: [''],
  });
  inquiryActions: string[] = [];

  private destroy$ = new Subject();
  private pinnedOfferId?: number;

  constructor(
    private fb: NonNullableFormBuilder,
    private rolesService: BaseRolesService,
    private queryParamsService: BaseQueryParamService,
    public filterService: BaseFilterService,
    private modalService: BaseModalService,
    private baseInquiryService: BaseInquiryService,
    private router: Router,
    private store: Store<BaseCoreState>,
    private baseInquiryDateService: BaseInquiryDateService,
    private baseInquiryShiftService: BaseInquiryShiftService,
    private activatedRoute: ActivatedRoute,
    private baseCustomerInquiryService: BaseCustomerInquiryService,
    private serviceProviderService: BaseCustomerServiceProviderSelectService,
    private sharedService: BaseCustomerSharedService
  ) {}

  ngOnInit() {
    this.canPublishInquiry = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PUBLISH_INQUIRY);
    this.hasCloseInquiryPermission = this.rolesService.hasCustomerFeaturePermission(
      BASE_PERMISSIONS.FEATURE_CLOSE_INQUIRY
    );

    this.pinnedOfferId = history.state?.pinnedOfferId;

    [this.listOrderKey, this.listOrderDirection] = this.filterService.setOrderParametersByQueryParameters();

    this.getInquiryActiveFilters();
    this.fetchFilteredOffers();
    this.selectCustomerInquiry();
    this.selectInquiryOffers();
    this.toggleSidebarVisibility();
    this.initFilterValuesByQueryParams();
    this.handleClosePageStatus();
  }

  ngOnDestroy() {
    this.store.dispatch(baseInquiryActions.resetCustomerInquiryOffersList());
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  sortOffersList(item: any): void {
    if (this.offers.length) {
      this.setOrderDirection(item);
      this.setQueryParams(item.orderKey, this.listOrderDirection);
      this.loadInquiryOffers();
    }
  }

  openNewStaffRequestModal(): void {
    this.modalRef = this.modalService.open(
      {
        headText: 'inquiry.new',
        bodyTemplate: this.staffRequest,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.modalRef.result.then(() => {
      this.isSidebarVisible = false;
    }, noop);
  }

  handleClick($event: string) {
    if ($event.includes('action.close')) {
      this.onClose();
    }
    if ($event.includes('action.publish')) {
      this.onPublish(this.inquiry?.id as number);
    }
  }

  onStickyChange(isSticky: boolean): void {
    this.isSticky = isSticky;
  }

  loadOffersNextPage(): void {
    if (this.pagination && this.pagination.page.current < this.pagination.page.total) {
      if (!this.inquiry) {
        return;
      }

      this.store.dispatch(
        baseInquiryActions.loadCustomerInquiryOffersList({
          payload: {
            ...this.activatedRoute.snapshot.queryParams,
            experience: BaseHelpersService.stringToBoolean(this.activatedRoute.snapshot.queryParams.experience),
            inquiry: this.inquiry.id,
            loadNextPage: true,
            page: this.pagination.page.current + InquiryOffersPagination.page,
          },
        })
      );
    }
  }

  copyInquiryHandler(event: Event) {
    if (!this.inquiry) {
      return;
    }

    event.stopPropagation();
    this.copyInquiry(this.inquiry.id);
  }

  openInquiredServiceProvidersModal(event: Event) {
    if (!this.inquiry) {
      return;
    }

    event.stopPropagation();
    this.store.dispatch(
      baseInquiryActions.loadInquiryProviders({
        payload: { inquiryId: this.inquiry.id },
      })
    );
    this.inquiredServiceProvidersModalRef = this.modalService.open(
      {
        headText: 'inquiry.inquiredServiceProvidersModal.headText',
        bodyTemplate: this.inquiredServiceProvidersModal,
        okButtonDisplayed: false,
        cancelButtonOnly: true,
        cancelText: 'action.close',
      },
      {
        backdrop: 'static',
        size: 'xl',
      }
    );
    this.inquiredServiceProvidersModalRef.result.then(noop, noop);
  }

  private copyInquiry(inquiryId: number) {
    this.baseCustomerInquiryService
      .getInquiry(inquiryId)
      .pipe(
        takeUntil(this.destroy$),
        map((inquiry) => inquiry as BaseInquiryType)
      )
      .subscribe((inquiry) => {
        this.copiedInquiry = { ...inquiry, id: -1 };

        const isMixedShifts = this.baseCustomerInquiryService.defineIsShiftTypesMixed(this.copiedInquiry);

        if (isMixedShifts) {
          this.baseCustomerInquiryService.openShiftViewPage(this.copiedInquiry, inquiry.id as number, true);
        } else {
          this.openNewStaffRequestModal();
        }
      });
  }

  private toggleSidebarVisibility(): void {
    this.baseInquiryService.toggleInquirySidebarSubject$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => (this.isSidebarVisible = visible));
  }

  private onPublish(inquiryId: number): void {
    this.unpublishedInquiryId = inquiryId;

    if (!!this.inquiry?.directlyInquired) {
      this.sharedService.publishInquiryDirectly(inquiryId, this.inquiry.directlyInquired.profile.pspBranches, true);
    } else {
      this.serviceProviderSelectModalRef = this.serviceProviderService.openInquiryVisibilityAndNotificationsModal(
        this.serviceProviderSelectModal
      );

      this.serviceProviderSelectModalRef.result.then(() => {
        this.updateCustomerInquiryStatus(BaseInquiryStatus.published);
      }, noop);
    }
  }

  private selectInquiryOffers() {
    this.store
      .select(baseInquirySelectors.getCustomerInquiryOffers)
      .pipe(
        takeUntil(this.destroy$),
        filter((response) => !!response.ok)
      )
      .subscribe((offersState) => {
        this.mainQualifications = offersState.filters?.mainQualifications as QualificationShortInfoResponse[];
        this.branches = offersState.filters?.branches as PspBranchFilterInfoResponse[];
        this.offers = offersState.data;
        this.filteredOffers = this.offers;
        this.pagination = offersState.pagination as MetaPagination;

        if (this.pinnedOfferId && this.offers.some((offer) => offer.data.id === this.pinnedOfferId)) {
          const offerId = this.pinnedOfferId;
          this.pinnedOfferId = undefined;
          this.store.dispatch(baseInquiryActions.selectCustomerInquiryOffer({ payload: { offerId } }));
        }
      });
  }

  private selectCustomerInquiry() {
    this.store
      .select(baseInquirySelectors.getCustomerInquiry)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((response) => response.data as BaseInquiryType)
      )
      .subscribe((inquiry) => {
        const inquiryDates = inquiry.dates as InquiryDateResponse[];

        this.shiftTimes = [];
        this.inquiry = inquiry;
        this.offersCount = inquiry.offersCount as OffersCountResponse;
        this.copiedInquiry = { ...this.inquiry };
        const currentDate = inquiryDates[0]?.date ? new Date(inquiryDates[0]?.date) : new Date();
        this.datesArray = this.baseInquiryDateService.prepareDates(inquiry, currentDate);

        if (this.inquiry.shiftTimes?.length) {
          this.shiftTimes = this.baseInquiryShiftService.addIndexAndLabelToShiftTimes(this.inquiry.shiftTimes);
          this.shiftTimes = this.baseInquiryShiftService.sortShiftTimes(this.shiftTimes);
        }
        this.shiftTimesWithFlex = this.baseInquiryShiftService.addFlexShiftToShiftTimes(this.shiftTimes);

        this.baseInquiryService.inquirySubject$.next(this.inquiry);
        this.baseInquiryShiftService.shiftTimesWithFlexSubject$.next(this.shiftTimesWithFlex);
        this.baseInquiryDateService.datesArraySubject$.next(this.datesArray);

        this.inquiryActions = this.baseCustomerInquiryService.setInquiryActions(
          this.inquiry,
          this.canPublishInquiry,
          this.hasCloseInquiryPermission
        );
      });
  }

  private initFilterValuesByQueryParams(): void {
    const filtersValues = this.filterService.setInitialFilterValuesByQueryParams(
      this.filtersForm.value,
      {
        [BaseInquiryOffersFilters.mainQualifications]: this.filterService.convertArrayToKeyValueObject(
          this.mainQualifications,
          'id',
          'name'
        ),
        [BaseInquiryOffersFilters.pspBranches]: this.filterService.convertArrayToKeyValueObject(
          this.branches,
          'id',
          'name'
        ),
        [BaseInquiryOffersFilters.rating]: this.queryParamsService.getQueryParam('rating'),
        [BaseInquiryOffersFilters.experience]: this.filterService.convertArrayToKeyValueObject(
          this.baseInquiryExperienceOption,
          'value',
          'label'
        ),
        [BaseInquiryOffersFilters.workCouncilStatus]: this.filterService.convertArrayToKeyValueObject(
          this.baseCustomerWCApprovalOptions,
          'value',
          'name'
        ),
      },
      ['rating', 'search']
    );

    this.hasFiltersValues = this.filterService.hasFilterValues(this.filtersForm.value);

    if (!this.hasFiltersValues) {
      return;
    }
    const statusValue = this.queryParamsService.getQueryParam('status');
    this.filtersForm.patchValue({
      ...filtersValues,
      experience: filtersValues.experience
        ? {
            value: JSON.parse(filtersValues.experience[0].value as string),
            label: filtersValues.experience[0].name,
          }
        : undefined,
      status: filtersValues.status
        ? this.baseOfferStatusOption.find((item) => item.id === parseInt(statusValue))
        : undefined,
    });
  }

  private onClose(): void {
    this.onInquiryCloseModalRef = this.modalService.open(
      {
        headText: 'inquiry.closeInquiry.headText',
        bodyTemplate: this.closeInquiryModal,
        hideFooter: true,
      },
      {
        scrollable: false,
        backdrop: true,
        size: 'lg',
      }
    );

    this.onInquiryCloseModalRef.result.then((value: string) => {
      this.updateCustomerInquiryStatus(BaseInquiryStatus.closed, value);
    }, noop);
  }

  private updateCustomerInquiryStatus(status: 1 | 2 | 3, closedNote?: string): void {
    if (!this.copiedInquiry) {
      return;
    }

    this.isSidebarVisible = false;

    this.store.dispatch(
      baseInquiryActions.updateInquiryStatus({
        payload: { inquiry: this.copiedInquiry.id as number, status, closedNote },
      })
    );
    this.router.navigate(['inquiries/list'], {
      queryParams: { status: `${BaseInquiryStatus.unpublished},${BaseInquiryStatus.published}` },
    });
  }

  private loadInquiryOffers(): void {
    if (!this.copiedInquiry) {
      return;
    }

    const queryParams = this.activatedRoute.snapshot.queryParams;

    const payload = {
      ...queryParams,
      inquiry: this.copiedInquiry.id as number,
      profileAverageRating: queryParams.rating ? parseInt(queryParams?.rating, 10) : undefined,
      experience: BaseHelpersService.stringToBoolean(this.activatedRoute.snapshot.queryParams.experience),
      pinnedOfferId: this.pinnedOfferId,
    };
    this.pinnedOfferId = undefined;
    this.store.dispatch(
      baseInquiryActions.loadCustomerInquiryOffersList({
        payload: payload,
      })
    );
  }

  private setQueryParams(orderKey?: string, orderDirection?: string): void {
    if (orderKey && orderDirection) {
      this.queryParamsService.addParameter('order', orderKey + ',' + orderDirection);
    } else {
      this.queryParamsService.removeParameter('order');
    }
  }

  private setOrderDirection(sortItem: BaseCustomerInquiryOffersSortKeysInterface): void {
    if (this.listOrderKey === sortItem.orderKey) {
      switch (this.listOrderDirection) {
        case BaseOrderDirectionEnum.asc:
          this.listOrderDirection = BaseOrderDirectionEnum.desc;
          break;
        case BaseOrderDirectionEnum.desc:
          this.listOrderDirection = BaseOrderDirectionEnum.asc;
          break;
        default:
          this.listOrderDirection = undefined;
          break;
      }
    } else {
      this.listOrderKey = sortItem.orderKey ? sortItem.orderKey : undefined;
      this.listOrderDirection = sortItem.orderDirection ? sortItem.orderDirection : undefined;
    }
  }

  private fetchFilteredOffers() {
    this.queryParamsService.filterSubject.pipe(takeUntil(this.destroy$), debounceTime(50)).subscribe((value) => {
      this.queryParamsService.redirectWithQueryParams(
        {
          ...value,
          filters: {
            ...value?.filters,
            experience: value?.filters?.experience?.value?.toString(),
            status: value?.filters?.status?.id?.toString(),
            workCouncilStatus: value?.filters?.workCouncilStatus?.value?.toString(),
          },
        },
        { search: 'value' },
        () => this.loadInquiryOffers()
      );
    });
  }

  private getInquiryActiveFilters(): void {
    this.store
      .select(baseInquirySelectors.getCustomerSelectedFilters)
      .pipe(
        takeUntil(this.destroy$),
        map((state) => state.data)
      )
      .subscribe((data) => {
        if (data) {
          this.inquiryListActiveFilters = { ...data.filters };

          for (let i in this.inquiryListActiveFilters) {
            this.inquiryListActiveFilters[i] = this.inquiryListActiveFilters[i].toString();
          }
        }
      });
  }

  private handleClosePageStatus(): void {
    this.store
      .select(baseInquirySelectors.getInquiryOffersCloseStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isClosed) => {
        if (isClosed) {
          this.router.navigate(['inquiries/list'], {
            queryParams:
              Object.entries(this.inquiryListActiveFilters).length === 0
                ? { status: `${BaseInquiryStatus.unpublished},${BaseInquiryStatus.published}` }
                : this.inquiryListActiveFilters,
          });
        }
      });
  }
}
